import { Component, OnInit, ViewChild, AfterViewInit } from "@angular/core";
import { finalize, map, shareReplay, switchMap, tap } from "rxjs/operators";
import { ActivatedRoute, Router } from "@angular/router";
import { CostService } from "src/app/shared/services/cost.service";
import { BehaviorSubject, combineLatest, forkJoin, Observable } from "rxjs";
import { CostStatuses, Cost } from "src/app/shared/models/cost.model";
import { TableDataSource } from "src/app/shared/components/table/table-data-source";
import { MatSort } from "@angular/material/sort";
import { tableSortFn } from "src/app/shared/utils/sort.util";
import { SelectionModel } from "@angular/cdk/collections";
import { CostActions } from "src/app/shared/models/cost-action.model";
import { PrintService } from "src/app/shared/services/print.service";
import { ToastrService } from "ngx-toastr";
import { FormBuilder } from "@angular/forms";
import { MatPaginator } from "@angular/material";
import { ServerDataSource } from "src/app/shared/models/server-data-source.model";
import { ApiDataOptions } from "src/app/shared/models/api-data.interface";
import { FileSaverService } from "ngx-filesaver";
import { ConfirmService } from "../../shared/services/confirm.service";
import { Redirect } from "../../shared/utils/redirect.util";
import { CostHistory } from "src/app/shared/models/cost-history.model";
// import SimpleBar from 'simplebar';

interface CostWithHistory extends Cost {
  history: CostHistory[];
}

@Component({
  selector: "app-costs",
  templateUrl: "./costs.component.html",
})
export class CostsComponent implements OnInit {
  costStatuses = CostStatuses;
  costStatus: string;
  private _costs$;
  costsByTab$;
  costsCount$;
  costStatusTab: string;
  routeParams$;
  actionsForCostStatus;

  dataSource = new TableDataSource<CostWithHistory>();
  dataSource2: ServerDataSource<Cost[]>;
  selection = new SelectionModel<Cost>(true, []);
  displayedColumns: string[] = [
    "select",
    "employee.person.lastName",
    "name",
    "status",
    "advance.name",
    "grossValue",
    "createdAt",
    "updatedAt",
    "action",
  ];
  costsForm;
  costHistoryLoaded: boolean = false;

  @ViewChild(MatSort, { static: false }) set sort(sort: MatSort) {
    this.dataSource.sort = sort;
  }

  @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
  @ViewChild("search-results", { static: false }) search;

  constructor(
    private _route: ActivatedRoute,
    private costService: CostService,
    private printService: PrintService,
    private router: Router,
    private toastrService: ToastrService,
    private fb: FormBuilder,
    private fs: FileSaverService,
    private confirmService: ConfirmService,
    private redirect: Redirect
  ) {}

  ngOnInit() {
    this.dataSource2 = new ServerDataSource(this.costService);

    this._costs$ = this.costService
      .fetchRequireAttentionCosts()
      .pipe(shareReplay(1));

    this.routeParams$ = this._route.params.subscribe(({ costStatus }) => {
      this.actionsForCostStatus = costStatus;
      this.selection.clear();
    });

    this.costsByTab$ = combineLatest([this._route.paramMap, this._costs$]).pipe(
      map(([paramsAsMap, costs]) =>
        (<Cost[]>costs).filter((cost) => {
          const costStatusTab = paramsAsMap.get("costStatus");
          this.costStatusTab = costStatusTab;
          let hasStatus = false;
          costStatusTab === "approve"
            ? (hasStatus =
                cost.status.name === CostStatuses.InApproval ||
                cost.status.name === CostStatuses.InMeritoricalApproval ||
                cost.status.name === CostStatuses.InFormalApproval)
            : (hasStatus = cost.status.name === costStatusTab);
          return hasStatus;
        })
      ),
      map((costs: CostWithHistory[]) => {
        this.dataSource.data = costs;
        this.dataSource.sortingDataAccessor = tableSortFn;
        this.costHistoryLoaded = true;
        return costs;
      })
    );

    this.costsCount$ = this._costs$.pipe(
      map((costs: Cost[]) =>
        costs.reduce(
          (totals, cost) => ({
            ...totals,
            [cost.status.name]: (totals[cost.status.name] || 0) + 1,
          }),
          {}
        )
      ),
      map((tabs) => {
        Object.values(CostStatuses).map((status) => {
          tabs[status] = tabs[status] || 0;
        });
        tabs["progress"] =
          tabs[CostStatuses.InApproval] +
          tabs[CostStatuses.InMeritoricalApproval] +
          tabs[CostStatuses.InFormalApproval];

        return tabs;
      })
    );
  }

  printCosts() {
    let costsId = this.selection.selected.map(({ id }) => new Cost(id));
    this.costService.changeCostStatus(1, CostActions.Print, costsId).subscribe(
      (response: Cost[]) => {
        this.printService.addCosts(response);
        this.router.navigate(["print/costs"]);
      },
      (_) => this.toastrService.error("Wystąpił problem z wydrukiem kosztów.")
    );
  }

  exportCosts() {
    let costsId = this.selection.selected.map(({ id }) => new Cost(id));
    const filename = Date.now() + ".csv";
    this.toastrService.success(
      "Za moment uruchomi się pobieranie pliku przelewu."
    );
    this.costService
      .changeCostStatus(1, CostActions.Export, costsId)
      .subscribe((response: Cost[]) => {
        this.redirect.to("/costs/printed");
        this.costService.exportToCsv(costsId).subscribe(
          (result) => {
            this.fs.save(result, filename);
          },
          (_) =>
            this.toastrService.error(
              "Wystąpił problem z eksportem plików przelewu."
            )
        );
      });
  }

  returnCosts() {
    let costsId = this.selection.selected.map(({ id }) => new Cost(id));
    this.costService.changeCostStatus(1, CostActions.Return, costsId).subscribe(
      (response: Cost[]) => {
        this.redirect.to("/costs/exported");
        this.toastrService.success("Koszty zostały poprawnie zwrócone.");
      },
      (_) => this.toastrService.error("Wystąpił problem ze zwrotem kosztów.")
    );
  }

  removeCost(cost: Cost) {
    if (cost.canRemove) {
      this.confirmService
        .openConfirmDialog(
          "Czy na pewno chcesz usunąć koszt? Zmiana będzie nieodwracalna."
        )
        .afterClosed()
        .subscribe((confirm) => {
          if (confirm) {
            this.costService.removeCost(cost.id).subscribe(
              (res) => {
                this.toastrService.success("Koszty został usunięty.");
                this.redirect.to();
              },
              (_) => this.toastrService.error("Koszt nie może zostać usunięty.")
            );
          }
        });
    }
  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.filter(
      (row) => row.canCurrentUserMakeAction === true
    ).length;
    return numSelected === numRows;
  }

  isAnySelected() {
    return this.selection.selected.length > 0;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    this.isAllSelected()
      ? this.selection.clear()
      : this.dataSource.data.forEach((row) =>
          row.canCurrentUserMakeAction ? this.selection.select(row) : null
        );
  }

  /** The label for the checkbox on the passed row */
  checkboxLabel(row?: Cost): string {
    if (!row) {
      return `${this.isAllSelected() ? "select" : "deselect"} all`;
    }
    return `${this.selection.isSelected(row) ? "deselect" : "select"} row ${
      row.id + 1
    }`;
  }
}
